import Button from '~/ui-apps/design-system/atoms/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

interface OnboardingStepFrameProps {
  headline: string;
  subtext?: string;
  children: ReactNode;
  continueLabel: string;
  onContinue: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
  isSubmitting?: boolean;
  error?: string | null;
  extraMessage?: string | null;
  contentClassName?: string;
  headerClassName?: string;
  headlineClassName?: string;
  subtextClassName?: string;
  errorClassName?: string;
  scrollableContent?: boolean;
  /** When there is no Back button, align Continue to the start, center, or end. Default: center. */
  continueAlign?: 'start' | 'center' | 'end';
}

export default function OnboardingStepFrame({
  headline,
  subtext,
  children,
  continueLabel,
  onContinue,
  onBack,
  canGoBack,
  isSubmitting,
  error,
  extraMessage,
  contentClassName,
  headerClassName,
  headlineClassName,
  subtextClassName,
  errorClassName,
  scrollableContent = true,
  continueAlign = 'center',
}: OnboardingStepFrameProps) {
  const showBackButton = Boolean(canGoBack && onBack);
  const continueOnlyJustify =
    continueAlign === 'end'
      ? 'justify-end'
      : continueAlign === 'start'
        ? 'justify-start'
        : 'justify-center';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={`shrink-0 pl-4 pt-3 sm:pt-10 pb-1 text-left mb-1 sm:mb-3 ${headerClassName ?? ''}`}>
        <h2
          className={`text-3xl font-bold tracking-tight text-gray-900 ${headlineClassName ?? ''}`}
        >
          {headline}
        </h2>
        {subtext ? (
          <p
            className={`my-2.5 text-xs sm:text-sm leading-snug text-gray-500 ${subtextClassName ?? ''}`}
          >
            {subtext}
          </p>
        ) : null}
      </div>

      <div
        className={
          scrollableContent
            ? `onboarding-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-2.5 md:px-6 pt-1 pb-4 ${contentClassName ?? ''}`
            : `relative z-10 flex min-h-0 flex-1 flex-col overflow-visible px-2 md:px-6 pt-2 pb-3 ${contentClassName ?? ''}`
        }
      >
        {children}
      </div>

      <div className="relative z-10 shrink-0 border-t border-gray-100 bg-white px-3 pb-4 pt-2 text-center shadow-[0_-4px_12px_rgba(255,255,255,0.9)]">
        {extraMessage && (
          <p className="text-sm text-amber-700 px-[5px] py-2 bg-amber-50 border border-amber-200 rounded-lg">
            {extraMessage}
          </p>
        )}
        {error && (
          <p
            role="alert"
            className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-red-600 ${errorClassName ?? ''}`}
          >
            <FontAwesomeIcon icon={faCircleExclamation} className="size-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
        )}

        <div
          className={`mt-2.5 flex w-full gap-2 ${
            showBackButton ? 'items-center justify-between' : continueOnlyJustify
          }`}
        >
          {showBackButton && (
            <Button
              type="outline"
              size="small"
              disabled={isSubmitting}
              onClick={onBack}
              className="!shrink-0 !px-4 !py-2 !border !border-gray-300 !font-medium text-gray-600 hover:!border-gray-400 hover:!bg-gray-50 hover:!text-gray-800"
            >
              Back
            </Button>
          )}
          <Button
            type="primary"
            size="small"
            fullWidth={false}
            disabled={isSubmitting}
            onClick={onContinue}
            className="!flex !w-auto !flex-none !items-center !justify-center !px-4 !py-2 !font-medium sm:min-w-[8rem]"
          >
            {isSubmitting ? (
              <span className="inline-flex items-center justify-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                <span>{continueLabel}</span>
              </span>
            ) : (
              continueLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
